import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";

const SKELETON_ROWS = ["alpha", "bravo", "charlie"];

type EquipmentAttributes = {
	imei?: string | null;
	serialNumber?: string | null;
};

type EquipmentSummary = {
	brand?: string | null;
	model?: string | null;
	attributes?: EquipmentAttributes | null;
};

type DeliveryRepairEntry = {
	id?: string | number | null;
	storeRepairIdentifier?: string | null;
	repairNumber?: string | number | null;
	updatedAt?: string | Date | null;
	clientEquipment?: EquipmentSummary | null;
	problemDescription?: string | null;
	repairObservation?: string | null;
	state?: string | null;
	fixed?: boolean | null;
	value?: number | null;
};

type ClientDeliveryTableProps = {
	repairs?: DeliveryRepairEntry[];
	loading?: boolean;
};

const formatEquipment = (equipment?: EquipmentSummary | null) => {
	if (!equipment) {
		return "-";
	}

	const brand = equipment.brand?.trim() ?? "";
	const model = equipment.model?.trim() ?? "";
	const label = `${brand} ${model}`.trim();
	return label.length > 0 ? label : "-";
};

const formatImei = (equipment?: EquipmentSummary | null) => {
	const attributes = equipment?.attributes;
	const imei = attributes?.imei ?? "";
	const serial = attributes?.serialNumber ?? "";
	const value = imei.length > 0 ? imei : serial;
	return value.length > 0 ? value : "-";
};

const formatDate = (value?: string | Date | null) => {
	if (!value) {
		return "-";
	}

	const date = typeof value === "string" ? new Date(value) : value;
	if (Number.isNaN(date.getTime())) {
		return "-";
	}

	return format(date, "yyyy/MM/dd");
};

const formatCurrency = (value: number) =>
	new Intl.NumberFormat("pt-PT", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(value);

const normalizeNumber = (value: number | null | undefined) => {
	const parsed = Number(value ?? 0);
	return Number.isNaN(parsed) ? 0 : parsed;
};

export function ClientDeliveryTable({
	repairs = [],
	loading = false,
}: ClientDeliveryTableProps) {
	const renderSkeleton = () => (
		<TableBody>
			{SKELETON_ROWS.map((rowKey) => (
				<TableRow key={`delivery-${rowKey}`} className="animate-pulse">
					<TableCell>
						<div className="h-4 w-16 rounded bg-muted/70" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-16 rounded bg-muted/60" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-32 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-28 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-32 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/30" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/30" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/30" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	return (
		<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/50">
						<TableHead className="w-[75px]">#Loja</TableHead>
						<TableHead className="w-[90px]">#Rep</TableHead>
						<TableHead className="w-[115px]">DataEntrada</TableHead>
						<TableHead className="w-[160px]">Equipamento</TableHead>
						<TableHead className="w-[160px]">IMEI/SN</TableHead>
						<TableHead>Problema</TableHead>
						<TableHead className="w-[200px]">Intervenção</TableHead>
						<TableHead className="w-[180px]">Estado</TableHead>
						<TableHead className="w-[200px]">Preço</TableHead>
					</TableRow>
				</TableHeader>
				{loading ? (
					renderSkeleton()
				) : (
					<TableBody>
						{repairs.map((repair) => {
							const rowKey = repair.id ?? repair.repairNumber ?? "delivery-row";
							const isFixed = repair.fixed !== false;
							const intervention =
								repair.state === "waiting_delivery"
									? (repair.repairObservation ?? "-")
									: "Orçamento aprovado";

							return (
								<TableRow key={rowKey}>
									<TableCell>{repair.storeRepairIdentifier ?? "-"}</TableCell>
									<TableCell>{repair.repairNumber ?? "-"}</TableCell>
									<TableCell>{formatDate(repair.updatedAt)}</TableCell>
									<TableCell>
										{formatEquipment(repair.clientEquipment)}
									</TableCell>
									<TableCell>{formatImei(repair.clientEquipment)}</TableCell>
									<TableCell>{repair.problemDescription ?? "-"}</TableCell>
									<TableCell>{intervention}</TableCell>
									<TableCell>
										{isFixed ? "Reparado" : "Não foi reparado"}
									</TableCell>
									<TableCell>
										{isFixed
											? formatCurrency(normalizeNumber(repair.value))
											: "-"}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				)}
			</Table>
		</div>
	);
}
